import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { PageLoader } from "../components/Spinner";
import StepNavigation from "../components/StepNavigation";
import ScriptStep from "../components/steps/ScriptStep";
import SceneReviewStep from "../components/steps/SceneReviewStep";
import AssetsStep from "../components/steps/AssetsStep";
import PreviewStep from "../components/steps/PreviewStep";
import EditorStep from "../components/steps/EditorStep";
import ScriptActions from "Redux/ScriptRedux";
import AudioActions from "Redux/AudioRedux";
import VideoActions from "Redux/VideoRedux";
import DraftActions from "Redux/DraftRedux";
import ThumbnailActions from "Redux/ThumbnailRedux";

const STEPS = {
  SCRIPT: 2,
  SCENES: 3,
  PREVIEW: 4,
  ASSETS: 5,
  EDITOR: 6,
};

export default function ScriptDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [currentStep, setCurrentStep] = useState(STEPS.SCRIPT);
  const [completedSteps, setCompletedSteps] = useState([1]); // Topic already completed
  
  const [topic, setTopic] = useState("");
  const [videoSettings, setVideoSettings] = useState({});
  const [script, setScript] = useState("");
  const [scriptId, setScriptId] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  
  const [audioStatus, setAudioStatus] = useState("pending");
  const [thumbnailStatus, setThumbnailStatus] = useState("pending");
  const [assetsProgress, setAssetsProgress] = useState(0);
  const [estimatedTimeLeft, setEstimatedTimeLeft] = useState("2-3 mins");
  
  const [error, setError] = useState("");
  
  const [audioUrl, setAudioUrl] = useState("");
  const [audioId, setAudioId] = useState(null);
  const [images, setImages] = useState([]);
  const [segments, setSegments] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoId, setVideoId] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSavingScript, setIsSavingScript] = useState(false);
  const [videoHistory, setVideoHistory] = useState([]);
  const [subtitleData, setSubtitleData] = useState("");
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);
  const [subtitleStyle, setSubtitleStyle] = useState("default");
  const [subtitleSettings, setSubtitleSettings] = useState(null);
  
  // Animation state
  const [animateVisuals, setAnimateVisuals] = useState(false);
  const [animationStats, setAnimationStats] = useState(null);
  const animationPollRef = useRef(null);
  
  // Sound effects state
  const [soundEffectClips, setSoundEffectClips] = useState([]);
  
  // Watermark state (based on team plan)
  const [showWatermark, setShowWatermark] = useState(true);
  
  const saveTimeoutRef = useRef(null);

  const loadVideoHistory = useCallback((scriptIdToLoad) => {
    if (!scriptIdToLoad) return;
    dispatch(VideoActions.videoListRequest(scriptIdToLoad, (data, error) => {
      if (error) {
        console.error("Failed to load video history:", error);
        return;
      }
      setVideoHistory(data || []);
    }));
  }, [dispatch]);

  // Animation polling
  const startAnimationPolling = useCallback((scriptIdToPoll) => {
    if (animationPollRef.current) {
      clearInterval(animationPollRef.current);
    }
    
    animationPollRef.current = setInterval(() => {
      dispatch(ThumbnailActions.animationBatchStatusRequest(scriptIdToPoll, (batchStatus, error) => {
        if (error) {
          console.error("Animation polling error:", error);
          return;
        }
        
        setAnimationStats(batchStatus.stats);
        
        // Update segments with latest animation data
        if (batchStatus.thumbnails) {
          setImages(prev => prev.map(img => {
            const updated = batchStatus.thumbnails.find(t => t.id === img.id);
            if (updated) {
              return {
                ...img,
                animation_status: updated.animation_status,
                animated_video_url: updated.animated_video_url,
                animation_prompt: updated.animation_prompt,
                animation_error_message: updated.animation_error_message,
              };
            }
            return img;
          }));
        }
        
        // Stop polling if all done
        if (batchStatus.stats.pending === 0 && batchStatus.stats.processing === 0) {
          clearInterval(animationPollRef.current);
          animationPollRef.current = null;
        }
      }));
    }, 3000);
  }, [dispatch]);

  // Cleanup animation polling on unmount
  useEffect(() => {
    return () => {
      if (animationPollRef.current) {
        clearInterval(animationPollRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (id) {
      loadScript(id);
    }
  }, [id]);

  const loadScript = (scriptIdToLoad) => {
    setIsLoading(true);
    setError("");
    
    dispatch(ScriptActions.scriptDetailRequest(scriptIdToLoad, (scriptData, loadError) => {
      if (loadError) {
        setError(loadError || "Failed to load script");
        console.error("Failed to load script:", loadError);
        setIsLoading(false);
        return;
      }
      
      setTopic(scriptData.topic || "");
      setScript(scriptData.content || "");
      setScriptId(scriptData.id);
      setVideoSettings({
        videoType: scriptData.video_type || "short",
        aspectRatio: scriptData.aspect_ratio || "9:16",
        duration: scriptData.duration || 30,
        contentStyle: scriptData.content_style || "informative",
        imageFrequency: scriptData.image_frequency || 10,
        subtitles: scriptData.subtitles ?? true,
        voice: scriptData.voice || "neutral",
        resolution: scriptData.resolution || "1080p",
        imageStyle: scriptData.image_style || "",
        characterImage: null,
        characterLabel: scriptData.character_label || "",
        useCharacterReference: scriptData.use_character_reference || false,
        voiceId: scriptData.voice_id || "af_bella",
        voiceName: scriptData.voice_name || "Bella",
      });
      updateWordCount(scriptData.content || "");

      const hasCompletedAudio = scriptData.audios?.some(a => a.status === "completed" && a.audio_url);
      const hasCompletedThumbnail = scriptData.thumbnail_images?.some(t => t.status === "completed" && t.image_url);
      const hasPendingAudio = scriptData.audios?.some(a => 
        a.status === "pending" || a.status === "processing" || a.status === "generating_prompt"
      );
      const hasPendingThumbnail = scriptData.thumbnail_images?.some(t => 
        t.status === "pending" || t.status === "processing" || t.status === "generating_prompt"
      );

      if (hasCompletedAudio && hasCompletedThumbnail) {
        const audio = scriptData.audios.find(a => a.status === "completed");
        const thumbnails = scriptData.thumbnail_images.filter(t => t.status === "completed" && t.image_url);
        
        setAudioUrl(audio.audio_url);
        setAudioId(audio.id);
        setSubtitleData(audio.subtitle_data || "");
        setAnimateVisuals(scriptData.animate_visuals || false);
        setShowWatermark(scriptData.show_watermark !== false);
        setImages(thumbnails.map((t, i) => ({ 
          id: t.id || i + 1, 
          image_url: t.image_url, 
          prompt: t.prompt,
          start_time: t.start_time,
          end_time: t.end_time,
          animation_status: t.animation_status,
          animation_prompt: t.animation_prompt,
          animated_video_url: t.animated_video_url,
          animation_error_message: t.animation_error_message,
        })));
        
        // If animate_visuals is enabled, fetch animation stats
        if (scriptData.animate_visuals) {
          dispatch(ThumbnailActions.animationBatchStatusRequest(scriptData.id, (batchStatus, batchError) => {
            if (batchError) {
              console.error("Failed to get animation status:", batchError);
              return;
            }
            setAnimationStats(batchStatus.stats);
            if (batchStatus.stats.pending > 0 || batchStatus.stats.processing > 0) {
              startAnimationPolling(scriptData.id);
            }
          }));
        }
        
        // Try to load saved draft segments
        dispatch(DraftActions.draftGetRequest(scriptData.id, (draft, draftError) => {
          let hasRestoredSegments = false;
          
          if (!draftError && draft && draft.segments && draft.segments.length > 0) {
            const restoredSegments = draft.segments.map((seg, i) => ({
              id: seg.id || `segment-${i}`,
              thumbnail_image_id: seg.thumbnail_image_id || seg.id,
              image_url: seg.image_url,
              prompt: seg.prompt,
              startTime: seg.start_time,
              endTime: seg.end_time,
              animated_video_url: seg.animated_video_url,
              animation_status: seg.animation_status,
              animation_prompt: seg.animation_prompt || "",
            }));
            setSegments(restoredSegments);
            hasRestoredSegments = true;
            
            if (draft.subtitles_enabled !== undefined) {
              setSubtitlesEnabled(draft.subtitles_enabled);
            }
            if (draft.subtitle_settings) {
              setSubtitleSettings(draft.subtitle_settings);
            }
            if (draft.sound_effect_clips && draft.sound_effect_clips.length > 0) {
              setSoundEffectClips(draft.sound_effect_clips);
            }
          }
          
          // If no draft segments, use thumbnail start/end times if available
          if (!hasRestoredSegments && thumbnails.some(t => t.start_time !== undefined)) {
            const segmentsFromThumbnails = thumbnails.map((t, i) => ({
              id: t.id || `segment-${i}`,
              thumbnail_image_id: t.id,
              image_url: t.image_url,
              prompt: t.prompt,
              startTime: t.start_time,
              endTime: t.end_time,
              animated_video_url: t.animated_video_url,
              animation_status: t.animation_status,
              animation_prompt: t.animation_prompt || "",
            }));
            setSegments(segmentsFromThumbnails);
          }
          
          setIsLoading(false);
        }));
        
        setCompletedSteps([1, STEPS.SCRIPT, STEPS.SCENES, STEPS.ASSETS]);
        
        const stepMap = {
          'script': STEPS.SCRIPT,
          'scenes': STEPS.SCENES,
          'preview': STEPS.PREVIEW,
          'editor': STEPS.EDITOR,
        };
        const backendStep = scriptData.current_step;
        if (backendStep && stepMap[backendStep]) {
          setCurrentStep(stepMap[backendStep]);
        } else {
          setCurrentStep(STEPS.PREVIEW);
        }
        
        loadVideoHistory(scriptData.id);
      } else if (hasPendingAudio || hasPendingThumbnail) {
        setCurrentStep(STEPS.ASSETS);
        setCompletedSteps([1, STEPS.SCRIPT]);
        setAudioStatus(hasPendingAudio ? "generating" : "completed");
        setThumbnailStatus(hasPendingThumbnail ? "generating" : "completed");
        setAssetsProgress(30);
        setEstimatedTimeLeft("1-2 mins");
        
        const pendingAudio = scriptData.audios?.find(a => 
          a.status === "pending" || a.status === "processing" || a.status === "generating_prompt"
        );
        
        if (pendingAudio) {
          dispatch(AudioActions.audioPollRequest(pendingAudio.id, 5000, (completedAudio, pollError) => {
            if (pollError) {
              setError(pollError || "Failed to generate assets");
              setAudioStatus("failed");
              setThumbnailStatus("failed");
              setCurrentStep(STEPS.SCRIPT);
              setIsLoading(false);
              return;
            }
            // Audio completed - reload script to get all data
            loadScript(scriptIdToLoad);
          }));
        } else {
          setIsLoading(false);
        }
      } else {
        setCompletedSteps([1]);
        setCurrentStep(STEPS.SCRIPT);
        setIsLoading(false);
      }
    }));
  };

  const updateWordCount = (text) => {
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    setWordCount(words);
  };

  const handleScriptChange = (newScript) => {
    setScript(newScript);
    updateWordCount(newScript);
  };

  const handleSaveScript = (content) => {
    if (!scriptId) return;
    
    setIsSavingScript(true);
    dispatch(ScriptActions.scriptUpdateContentRequest(scriptId, content, (data, error) => {
      setIsSavingScript(false);
      if (error) {
        console.error("Failed to save script:", error);
      }
    }));
  };

  const handleRegenerateScript = () => {
    if (!scriptId) return;

    setIsRegenerating(true);
    setError("");

    dispatch(ScriptActions.scriptRegenerateRequest(scriptId, (data, regenError) => {
      if (regenError) {
        setError(regenError || "Failed to regenerate script");
        console.error("Script regeneration error:", regenError);
        setIsRegenerating(false);
        return;
      }

      setScript("");
      setWordCount(0);

      dispatch(ScriptActions.scriptPollRequest(scriptId, (completed, pollError) => {
        setIsRegenerating(false);
        
        if (pollError) {
          setError(pollError || "Failed to regenerate script");
          console.error("Script regeneration error:", pollError);
          return;
        }
        
        setScript(completed.content);
        updateWordCount(completed.content);
      }));
    }));
  };

  const handleBackToTopic = () => {
    navigate("/");
  };

  const handleGenerateAssets = () => {
    if (!scriptId) return;

    setCurrentStep(STEPS.ASSETS);
    setAssetsProgress(0);
    setAudioStatus("pending");
    setThumbnailStatus("pending");
    setEstimatedTimeLeft("2-3 mins");
    setError("");

    setAudioStatus("generating");
    setThumbnailStatus("generating");
    setAssetsProgress(10);

    const options = {
      voiceId: videoSettings.voiceId,
      voiceName: videoSettings.voiceName,
    };

    dispatch(AudioActions.audioGenerateRequest(scriptId, options, (audio, audioError) => {
      if (audioError) {
        setError(audioError || "Failed to generate assets");
        console.error("Asset generation error:", audioError);
        setAudioStatus("failed");
        setThumbnailStatus("failed");
        return;
      }

      setAudioUrl(audio.audio_url);
      setAudioId(audio.id);
      setSubtitleData(audio.subtitle_data || "");
      setAudioStatus("completed");
      setAssetsProgress(75);
      setEstimatedTimeLeft("30-60 secs");
      
      // Poll until thumbnail_images are ready
      let attempts = 0;
      const maxAttempts = 60;
      
      const pollThumbnails = () => {
        dispatch(ScriptActions.scriptDetailRequest(scriptId, (scriptData, scriptError) => {
          if (scriptError) {
            setError(scriptError || "Failed to load thumbnails");
            setThumbnailStatus("failed");
            return;
          }
          
          const allThumbnails = scriptData.thumbnail_images || [];
          const completedThumbnails = allThumbnails.filter(t => t.status === "completed" && t.image_url);
          const failedThumbnails = allThumbnails.filter(t => t.status === "failed");
          const pendingThumbnails = allThumbnails.filter(t => 
            t.status === "pending" || t.status === "processing" || t.status === "generating_prompt"
          );
          
          if (allThumbnails.length > 0) {
            const thumbnailProgress = (completedThumbnails.length / allThumbnails.length) * 20;
            setAssetsProgress(Math.min(95, 75 + thumbnailProgress));
          }
          
          // Done when no pending remain (include both completed + failed in scenes)
          if (pendingThumbnails.length === 0 && (completedThumbnails.length > 0 || failedThumbnails.length > 0)) {
            const allFinishedThumbnails = allThumbnails.filter(t => 
              (t.status === "completed" && t.image_url) || t.status === "failed"
            );
            finishWithThumbnails(allFinishedThumbnails, scriptData);
            return;
          }
          
          attempts++;
          if (attempts >= maxAttempts) {
            const allFinishedThumbnails = allThumbnails.filter(t => 
              (t.status === "completed" && t.image_url) || t.status === "failed"
            );
            if (allFinishedThumbnails.length > 0) {
              finishWithThumbnails(allFinishedThumbnails, scriptData);
            } else {
              setError("No thumbnail images were generated. Please try again.");
              setThumbnailStatus("failed");
            }
            return;
          }
          
          // Keep polling
          setTimeout(pollThumbnails, 3000);
        }));
      };
      
      const finishWithThumbnails = (thumbnails, scriptData) => {
        setImages(thumbnails.map((t, i) => ({
          id: t.id || i + 1,
          image_url: t.image_url,
          prompt: t.prompt,
          start_time: t.start_time,
          end_time: t.end_time,
          status: t.status,
          error_message: t.error_message,
          animation_status: t.animation_status,
          animation_prompt: t.animation_prompt,
          animated_video_url: t.animated_video_url,
          animation_error_message: t.animation_error_message,
        })));
        
        setThumbnailStatus("completed");
        setAssetsProgress(95);
        
        // Try to load draft segments
        dispatch(DraftActions.draftGetRequest(scriptId, (draft, draftError) => {
          let hasRestoredSegments = false;
          
          if (!draftError && draft && draft.segments && draft.segments.length > 0) {
            const restoredSegments = draft.segments.map((seg, i) => ({
              id: seg.id || `segment-${i}`,
              thumbnail_image_id: seg.thumbnail_image_id || seg.id,
              image_url: seg.image_url,
              prompt: seg.prompt,
              startTime: seg.start_time,
              endTime: seg.end_time,
              animated_video_url: seg.animated_video_url,
              animation_status: seg.animation_status,
              animation_prompt: seg.animation_prompt || "",
            }));
            setSegments(restoredSegments);
            hasRestoredSegments = true;
            
            if (draft.subtitles_enabled !== undefined) {
              setSubtitlesEnabled(draft.subtitles_enabled);
            }
            if (draft.sound_effect_clips && draft.sound_effect_clips.length > 0) {
              setSoundEffectClips(draft.sound_effect_clips);
            }
          }
          
          if (!hasRestoredSegments) {
            setSegments(thumbnails.map((t, i) => ({
              id: t.id || `segment-${i}`,
              thumbnail_image_id: t.id,
              image_url: t.image_url,
              prompt: t.prompt,
              startTime: t.start_time || 0,
              endTime: t.end_time || 0,
              status: t.status,
              error_message: t.error_message,
              animation_status: t.animation_status,
              animation_prompt: t.animation_prompt || "",
              animated_video_url: t.animated_video_url,
              animate_enabled: t.animation_status === "completed",
            })));
          }
          
          // Check animations
          if (scriptData && scriptData.animate_visuals) {
            setAnimateVisuals(true);
            dispatch(ThumbnailActions.animationBatchStatusRequest(scriptId, (batchStatus, batchError) => {
              if (!batchError && batchStatus) {
                setAnimationStats(batchStatus.stats);
                if (batchStatus.stats.pending > 0 || batchStatus.stats.processing > 0) {
                  startAnimationPolling(scriptId);
                }
              }
            }));
          }

          setAssetsProgress(100);
          setCompletedSteps([1, STEPS.SCRIPT, STEPS.ASSETS]);
          setCurrentStep(STEPS.SCENES);
          
          // Update backend with current step
          dispatch(ScriptActions.scriptUpdateStepRequest(scriptId, 'scenes', (data, stepErr) => {
            if (stepErr) {
              console.error("Failed to update script step:", stepErr);
            }
          }));
        }));
      };
      
      // Start polling for thumbnails
      pollThumbnails();
    }));
  };

  const handleCancelGeneration = () => {
    setCurrentStep(STEPS.SCRIPT);
    setAudioStatus("pending");
    setThumbnailStatus("pending");
    setAssetsProgress(0);
  };

  const debouncedSaveDraft = useCallback((scriptIdToSave, segmentsToSave, subtitlesEnabledValue, sfxClips = []) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      if (!scriptIdToSave || !segmentsToSave || segmentsToSave.length === 0) return;
      
      setIsSavingDraft(true);
      const options = { 
        subtitlesEnabled: subtitlesEnabledValue,
        subtitleSettings: subtitleSettings,
        soundEffectClips: sfxClips,
      };
      
      dispatch(DraftActions.draftSaveRequest(scriptIdToSave, audioUrl, segmentsToSave, options, (data, error) => {
        setIsSavingDraft(false);
        if (error) {
          console.error("Failed to save draft:", error);
        }
      }));
    }, 2000);
  }, [audioUrl, subtitleSettings, dispatch]);

  const handleSegmentsChange = (newSegments) => {
    setSegments(newSegments);
    if (scriptId && newSegments.length > 0) {
      debouncedSaveDraft(scriptId, newSegments, subtitlesEnabled, soundEffectClips);
    }
  };

  const handleSubtitlesChange = (enabled) => {
    setSubtitlesEnabled(enabled);
    if (scriptId && segments.length > 0) {
      debouncedSaveDraft(scriptId, segments, enabled, soundEffectClips);
    }
  };

  const handleSoundEffectClipsChange = (newClips) => {
    setSoundEffectClips(newClips);
    if (scriptId && segments.length > 0) {
      debouncedSaveDraft(scriptId, segments, subtitlesEnabled, newClips);
    }
  };

  const handleSubtitleSettingsChange = (newSettings) => {
    setSubtitleSettings(newSettings);
    if (scriptId && segments.length > 0) {
      debouncedSaveDraft(scriptId, segments, subtitlesEnabled, soundEffectClips);
    }
  };

  const handleExportVideo = (options = {}) => {
    if (!audioUrl || segments.length === 0 || !scriptId) return;

    setIsExporting(true);
    setCurrentStep(STEPS.ASSETS);
    setAssetsProgress(10);
    setEstimatedTimeLeft("2-3 mins");
    setError("");

    const videoOptions = { 
      subtitlesEnabled: options.subtitlesEnabled || false,
      subtitleStyle: options.subtitleStyle || subtitleStyle || 'default',
      subtitleSettings: options.subtitleSettings || null,
      soundEffectClips: soundEffectClips,
    };

    dispatch(VideoActions.videoGenerateRequest(scriptId, audioUrl, segments, videoOptions, (video, videoError) => {
      setIsExporting(false);
      
      if (videoError) {
        setError(videoError || "Failed to generate video");
        console.error("Video generation error:", videoError);
        setCurrentStep(STEPS.PREVIEW);
        return;
      }

      setVideoUrl(video.video_url);
      setVideoId(video.id);
      setAssetsProgress(100);
      setCompletedSteps([1, STEPS.SCRIPT, STEPS.ASSETS, STEPS.PREVIEW]);
      
      // Refresh video history after successful export
      loadVideoHistory(scriptId);
      
      setCurrentStep(STEPS.PREVIEW);
      
      // Delete draft after successful export
      dispatch(DraftActions.draftDeleteRequest(scriptId, (data, deleteError) => {
        if (deleteError) {
          console.error("Failed to delete draft segments:", deleteError);
        }
      }));
    }));
  };

  const handleBackToScript = () => {
    setCurrentStep(STEPS.SCRIPT);
  };

  const handleBackToScenes = () => {
    // Refetch script data to get latest thumbnail/animation data
    if (scriptId) {
      dispatch(ScriptActions.scriptDetailRequest(scriptId, (scriptData, error) => {
        if (error) {
          console.error("Failed to refresh script data:", error);
          setCurrentStep(STEPS.SCENES);
          return;
        }
        
        const thumbnails = scriptData.thumbnail_images?.filter(t => t.status === "completed" && t.image_url) || [];
        
        if (thumbnails.length > 0) {
          setImages(thumbnails.map((t, i) => ({ 
            id: t.id || i + 1, 
            image_url: t.image_url, 
            prompt: t.prompt,
            start_time: t.start_time,
            end_time: t.end_time,
            animation_status: t.animation_status,
            animation_prompt: t.animation_prompt,
            animated_video_url: t.animated_video_url,
            animation_error_message: t.animation_error_message,
          })));
          
          // Update segments with fresh data from thumbnails
          setSegments(thumbnails.map((t, i) => ({
            id: t.id || `segment-${i}`,
            thumbnail_image_id: t.id,
            image_url: t.image_url,
            prompt: t.prompt,
            startTime: t.start_time || 0,
            endTime: t.end_time || 0,
            animation_status: t.animation_status,
            animation_prompt: t.animation_prompt || "",
            animated_video_url: t.animated_video_url,
            animate_enabled: t.animation_status === "completed",
          })));
        }
        
        setCurrentStep(STEPS.SCENES);
      }));
    } else {
      setCurrentStep(STEPS.SCENES);
    }
  };

  const handleCreateAnother = () => {
    navigate("/");
  };

  // Scene Review handlers
  const handleScenesChange = (updatedScenes) => {
    // Update both images and segments with the new scene data
    setImages(updatedScenes.map((scene, i) => ({
      id: scene.id || i + 1,
      image_url: scene.image_url,
      prompt: scene.prompt,
      start_time: scene.startTime,
      end_time: scene.endTime,
      animation_status: scene.animation_status,
      animated_video_url: scene.animated_video_url,
      animate_enabled: scene.animate_enabled,
      animation_prompt: scene.animation_prompt || "",
    })));
    setSegments(updatedScenes);
  };

  const handleConfirmScenes = () => {
    // Mark scenes step as completed and move to preview
    setCompletedSteps([1, STEPS.SCRIPT, STEPS.SCENES, STEPS.ASSETS]);
    setCurrentStep(STEPS.PREVIEW);
    
    // Update backend with current step
    if (scriptId) {
      dispatch(ScriptActions.scriptUpdateStepRequest(scriptId, 'preview', (data, error) => {
        if (error) {
          console.error("Failed to update script step:", error);
        }
      }));
    }
  };

  const handleBackToAssets = () => {
    setCurrentStep(STEPS.ASSETS);
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <PageLoader label="Loading script…" />
      </div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.SCRIPT:
        return (
          <ScriptStep
            script={script}
            onScriptChange={handleScriptChange}
            onSaveScript={handleSaveScript}
            onRegenerate={handleRegenerateScript}
            onBack={handleBackToTopic}
            onNext={handleGenerateAssets}
            isRegenerating={isRegenerating}
            isSaving={isSavingScript}
            wordCount={wordCount}
            scriptId={scriptId}
          />
        );
      case STEPS.ASSETS:
        return (
          <AssetsStep
            progress={assetsProgress}
            estimatedTimeLeft={estimatedTimeLeft}
            audioStatus={audioStatus}
            thumbnailStatus={thumbnailStatus}
            onCancel={handleCancelGeneration}
          />
        );
      case STEPS.SCENES:
        // Merge segment data with image data to ensure prompts and thumbnail_image_id are always available
        const scenesData = segments.length > 0 
          ? segments.map((seg, i) => {
              // Find matching image by thumbnail_image_id, id, or index
              const matchingImage = images.find(img => 
                img.id === seg.thumbnail_image_id || 
                img.id === seg.id ||
                (seg.image_url && img.image_url === seg.image_url)
              ) || images[i];
              // Use thumbnail_image_id from segment, or from matching image, or fall back to matching image's id
              const thumbnailId = seg.thumbnail_image_id || matchingImage?.id;
              return {
                id: seg.id,
                thumbnail_image_id: thumbnailId,
                image_url: seg.image_url,
                prompt: seg.prompt || matchingImage?.prompt || "",
                startTime: seg.startTime,
                endTime: seg.endTime,
                animation_status: seg.animation_status || matchingImage?.animation_status,
                animated_video_url: seg.animated_video_url || matchingImage?.animated_video_url,
                animate_enabled: seg.animate_enabled || false,
                animation_prompt: seg.animation_prompt || matchingImage?.animation_prompt || "",
              };
            })
          : images.map((img, i) => ({
              id: img.id,
              thumbnail_image_id: img.id,
              image_url: img.image_url,
              prompt: img.prompt,
              startTime: img.start_time || 0,
              endTime: img.end_time || 0,
              animation_status: img.animation_status,
              animated_video_url: img.animated_video_url,
              animate_enabled: img.animate_enabled || false,
              animation_prompt: img.animation_prompt || "",
            }));
        return (
          <SceneReviewStep
            scenes={scenesData}
            audioUrl={audioUrl}
            aspectRatio={videoSettings.aspectRatio}
            onScenesChange={handleScenesChange}
            onConfirm={handleConfirmScenes}
            onBack={handleBackToScript}
          />
        );
      case STEPS.PREVIEW:
        return (
          <PreviewStep
            audioUrl={audioUrl}
            audioId={audioId}
            images={images}
            savedSegments={segments}
            topic={topic}
            aspectRatio={videoSettings.aspectRatio}
            onSegmentsChange={handleSegmentsChange}
            onExportVideo={handleExportVideo}
            onBack={handleBackToScenes}
            isExporting={isExporting}
            isSavingDraft={isSavingDraft}
            videoHistory={videoHistory}
            videoId={videoId}
            videoUrl={videoUrl}
            scriptId={scriptId}
            onRefreshHistory={() => loadVideoHistory(scriptId)}
            subtitleData={subtitleData}
            subtitlesEnabled={subtitlesEnabled}
            onSubtitlesChange={handleSubtitlesChange}
            subtitleStyle={subtitleStyle}
            onSubtitleStyleChange={setSubtitleStyle}
            animateVisuals={animateVisuals}
            animationStats={animationStats}
            soundEffectClips={soundEffectClips}
            onSoundEffectClipsChange={handleSoundEffectClipsChange}
            showWatermark={showWatermark}
            initialSubtitleSettings={subtitleSettings}
            onSubtitleSettingsChange={handleSubtitleSettingsChange}
          />
        );
      case STEPS.EDITOR:
        return (
          <EditorStep
            videoUrl={videoUrl}
            videoId={videoId}
            topic={topic}
            onCreateAnother={handleCreateAnother}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <StepNavigation currentStep={currentStep} completedSteps={completedSteps} />
        </div>
      </div>
      {error && currentStep !== STEPS.ASSETS && (
        <div className="max-w-4xl mx-auto px-6 pt-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
              <p className="text-sm text-red-600">{error}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => loadScript(id)}
                className="text-xs font-medium text-red-600 hover:text-red-800 underline"
              >
                Retry
              </button>
              <button
                onClick={() => setError("")}
                className="text-red-400 hover:text-red-600 p-0.5"
                aria-label="Dismiss"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      {renderStep()}
    </>
  );
}
